import { useEffect, useRef, useState } from 'react';
import { Copy, Download, Share2, Gift, Check, ChevronDown, X, Sparkles } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import api from '../../../services/api';
import { renderReferralVisual, canvasToFile } from '../../../components/referral/referralVisual';
import BrandLogo from '../../../components/ui/BrandLogo';

// Concours nommé sur le visuel : choix mémorisé sur cet appareil (« » = aucun concours précis).
const TARGET_KEY = 'mq_ref_target';
function resolveTarget(info, key) {
    const list = info?.targets || [];
    return list.find(t => t.key === key) || list[0] || { key: '', label: null, link: info?.link };
}
// « Concours CRPE : » pour un concours, « ISTQB — CTFL Foundation : » pour une thématique communautaire.
const headingOf = (t) => (t.label ? `${t.prefix ? `${t.prefix} ` : ''}${t.label} :` : null);

/**
 * Parrainage (migration 178). Règles : quand une personne s'abonne avec ton code, vous
 * gagnez chacun une semaine (la tienne s'ajoute à ton abonnement payé en cours, ou est
 * gardée pour ton prochain achat). Sans limite. Une inscription seule ne rapporte rien.
 */
export default function CompteParrainage() {
    const [info, setInfo] = useState(null);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(null);
    const [visualReady, setVisualReady] = useState(false);
    const [codeInput, setCodeInput] = useState('');
    const [codeMsg, setCodeMsg] = useState(null);
    const canvasRef = useRef(null);
    const [zoomSrc, setZoomSrc] = useState(null); // aperçu agrandi du visuel
    useEffect(() => {
        if (!zoomSrc) return;
        const onKey = (e) => { if (e.key === 'Escape') setZoomSrc(null); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [zoomSrc]);
    // ?t=scope:<slug> | parcours:<slug> : arrivée depuis l'encart d'une thématique, qui la présélectionne.
    const [params] = useSearchParams();
    const fromScope = params.get('scope') || '';
    const [targetKey, setTargetKey] = useState(() => {
        const t = params.get('t');
        try {
            if (t !== null) { localStorage.setItem(TARGET_KEY, t); return t; }
            return localStorage.getItem(TARGET_KEY) || '';
        } catch { return t || ''; }
    });
    const chooseTarget = (key) => {
        setTargetKey(key);
        try { localStorage.setItem(TARGET_KEY, key); } catch { /* pas de stockage */ }
    };

    useEffect(() => {
        api.get('/user/referral.php', { params: fromScope ? { scope: fromScope } : {} })
            .then(({ data }) => setInfo(data.data))
            .catch(() => setError('Parrainage indisponible pour le moment.'));
    }, []);

    useEffect(() => {
        if (!info || !canvasRef.current) return;
        const t = resolveTarget(info, targetKey);
        setVisualReady(false);
        renderReferralVisual(canvasRef.current, {
            code: info.code, link: t.link, heading: headingOf(t),
            dgfip: t.key.startsWith('scope:fonction-publique-etat') || t.key.startsWith('parcours:dgfip-'),
        })
            .then(() => setVisualReady(true))
            .catch(() => setVisualReady(false));
    }, [info, targetKey]);

    if (error) return <p className="compte-purchase-dates">{error}</p>;
    if (!info) return <p className="compte-purchase-dates">Chargement…</p>;

    const target = resolveTarget(info, targetKey);
    const link = target.link;
    const shareText = `Je prépare ${target.label ? (target.prefix ? `le concours ${target.label}` : target.label) : 'mon concours'} sur MonQuiz : des QCM corrigés et des révisions au bon moment. Avec mon code ${info.code}, tu as 7 jours offerts sur ton abonnement.`;

    const copy = async (what, value) => {
        try { await navigator.clipboard.writeText(value); setCopied(what); setTimeout(() => setCopied(null), 1800); } catch { /* pas de presse-papiers */ }
    };

    const download = async () => {
        const file = await canvasToFile(canvasRef.current, `monquiz-parrainage-${info.code}.png`);
        if (!file) return;
        const url = URL.createObjectURL(file);
        const a = document.createElement('a');
        a.href = url; a.download = file.name;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    // Partage natif (mobile surtout) avec l'image ; sinon on télécharge l'image et on copie le texte.
    const share = async () => {
        const file = await canvasToFile(canvasRef.current, `monquiz-parrainage-${info.code}.png`);
        const payload = { title: 'MonQuiz', text: `${shareText} ${link}` };
        try {
            if (file && navigator.canShare?.({ files: [file] })) {
                await navigator.share({ ...payload, files: [file] });
                return;
            }
            if (navigator.share) { await navigator.share({ ...payload, url: link }); return; }
        } catch (e) {
            if (e?.name === 'AbortError') return; // partage annulé
        }
        await download();
        copy('texte', `${shareText} ${link}`);
    };

    const submitCode = async (e) => {
        e.preventDefault();
        setCodeMsg(null);
        try {
            const { data } = await api.post('/user/referral.php', { code: codeInput });
            setInfo(data.data);
            setCodeMsg({ ok: true, text: 'Code enregistré : 7 jours offerts s’ajouteront à ton premier abonnement.' });
        } catch (err) {
            setCodeMsg({ ok: false, text: err?.response?.data?.message || 'Code refusé.' });
        }
    };

    return (
        <div className="compte-parrainage">
            <header className="parrainage-header">
                <h3 className="compte-section-label">Parrainage</h3>
                <h2>7 jours offerts, pour toi et ton ami</h2>
                <p>Dès son premier abonnement payé avec ton code, vous gagnez chacun une semaine.</p>
            </header>

            <section className="parrainage-invite" aria-label="Inviter un ami">
                <figure className="parrainage-visual">
                    <button type="button" className="parrainage-visual__zoom" disabled={!visualReady}
                        onClick={() => setZoomSrc(canvasRef.current.toDataURL('image/png'))} title="Agrandir le visuel">
                        <canvas ref={canvasRef} className="parrainage-visual__canvas" aria-label={`Visuel de parrainage avec le code ${info.code}`} />
                    </button>
                    <figcaption>Ton code et ton QR code sont déjà intégrés. Clique pour agrandir.</figcaption>
                    {zoomSrc && createPortal(
                        <div className="parrainage-zoom" role="dialog" aria-label="Visuel agrandi" onClick={() => setZoomSrc(null)}>
                            <button type="button" className="parrainage-zoom__close" aria-label="Fermer"><X size={22} /></button>
                            <img src={zoomSrc} alt={`Visuel de parrainage avec le code ${info.code}`} onClick={e => e.stopPropagation()} />
                        </div>,
                        document.body,
                    )}
                </figure>
                <div className="parrainage-invite__content">
                    <span className="parrainage-bonus"><Gift size={16} aria-hidden="true" /> +7 jours chacun</span>
                    <h3>Ton invitation est prête</h3>
                    <p className="parrainage-invite__intro">Partage cette image avec un ami qui prépare son concours, ou envoie-lui directement ton lien.</p>
                    {(info.targets || []).length > 1 && (
                        <div className="parrainage-targets" role="radiogroup" aria-label="Concours affiché sur le visuel">
                            <span className="parrainage-targets__label">Concours affiché sur le visuel</span>
                            <div className="parrainage-targets__list">
                                {info.targets.map(t => {
                                    const active = t.key === target.key;
                                    return (
                                        <button key={t.key || 'aucun'} type="button" role="radio" aria-checked={active}
                                            className={`parrainage-target-card${active ? ' is-active' : ''}${t.image ? ' has-image' : ''}${!t.key ? ' is-all' : ''}`}
                                            style={t.image ? { backgroundImage: `url(${t.image})` } : (t.color ? { '--target-color': t.color } : undefined)}
                                            onClick={() => chooseTarget(t.key)}>
                                            {!t.key && <span className="parrainage-target-card__logo"><BrandLogo width={86} hideTagline monColor="#cbd5e1" /></span>}
                                            {t.key && !t.image && <Sparkles size={18} aria-hidden="true" />}
                                            <span className="parrainage-target-card__label">{t.label || 'Tous concours'}</span>
                                            {active && <span className="parrainage-target-card__check"><Check size={13} strokeWidth={3} /></span>}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    <div className="parrainage-code">
                        <span className="parrainage-code__label">Ton code de parrainage</span>
                        <strong className="parrainage-code__value">{info.code}</strong>
                    </div>
                    <div className="parrainage-invite__actions">
                        <button type="button" className="btn btn-primary" disabled={!visualReady} onClick={share}>
                            <Share2 size={16} /> Partager le visuel
                        </button>
                        <button type="button" className="btn btn-ghost" onClick={() => copy('lien', link)}>
                            {copied === 'lien' ? <Check size={16} /> : <Copy size={16} />} {copied === 'lien' ? 'Lien copié' : 'Copier mon lien'}
                        </button>
                    </div>
                    <div className="parrainage-invite__tools">
                        <button type="button" className="btn btn-ghost btn-sm" disabled={!visualReady} onClick={download}>
                            <Download size={14} /> Télécharger l’image
                        </button>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => copy('texte', `${shareText} ${link}`)}>
                            {copied === 'texte' ? <Check size={14} /> : <Copy size={14} />} {copied === 'texte' ? 'Message copié' : 'Copier le message'}
                        </button>
                    </div>
                    <p className="parrainage-visual__hint">Sur ordinateur, « Partager » télécharge l’image et copie le message avec ton lien.</p>
                    <p className="parrainage-invite__hint"><Gift size={16} aria-hidden="true" /><span>Ta semaine prolonge ton abonnement payé en cours ou reste en réserve pour ton prochain achat. Sans limite de parrainages.</span></p>
                    <span className="parrainage-sr-only" role="status">{copied ? (copied === 'lien' ? 'Lien copié' : 'Message copié') : ''}</span>
                </div>
            </section>

            <section className="parrainage-results" aria-labelledby="parrainage-results-title">
                <h3 id="parrainage-results-title">Ton parrainage en chiffres</h3>
                <div className="parrainage-stats">
                    <div><strong>{info.signups}</strong><span>Inscrit{info.signups > 1 ? 's' : ''}</span></div>
                    <div><strong>{info.subscribers}</strong><span>Abonné{info.subscribers > 1 ? 's' : ''}</span></div>
                    <div><strong>{info.weeks_applied}</strong><span>Semaine{info.weeks_applied > 1 ? 's' : ''} ajoutée{info.weeks_applied > 1 ? 's' : ''}</span></div>
                    <div><strong>{info.weeks_reserved}</strong><span>Semaine{info.weeks_reserved > 1 ? 's' : ''} en réserve</span></div>
                </div>
                {info.signups === 0 && <p className="parrainage-results__empty">Invite ton premier ami pour commencer à gagner des semaines.</p>}
            </section>

            {info.referred && (
                <p className="compte-purchase-dates">
                    Tu as été parrainé{info.referrer_prenom ? ` par ${info.referrer_prenom}` : ''}.
                    {info.bonus_pending ? ' 7 jours offerts s’ajouteront à ton premier abonnement.' : ''}
                </p>
            )}
            {info.can_enter_code && (
                <details className="parrainage-received">
                    <summary>
                        <span>Tu as reçu un code de parrainage ?</span>
                        <ChevronDown size={16} className="parrainage-details-chevron" aria-hidden="true" />
                    </summary>
                    <form className="parrainage-enter" onSubmit={submitCode}>
                        <label htmlFor="parrainage-code-input">Ton code reçu</label>
                        <div className="parrainage-enter__row">
                            <input id="parrainage-code-input" value={codeInput} maxLength={16} placeholder="AMI-XXXXXX"
                                autoCapitalize="characters" autoComplete="off" spellCheck={false}
                                onChange={e => setCodeInput(e.target.value.toUpperCase())} />
                            <button type="submit" className="btn btn-sm btn-primary" disabled={!codeInput.trim()}>Valider</button>
                        </div>
                    </form>
                </details>
            )}
            {codeMsg && <p role={codeMsg.ok ? 'status' : 'alert'} className="parrainage-code-message" style={{ color: codeMsg.ok ? 'var(--success)' : 'var(--danger)' }}>{codeMsg.text}</p>}

            <p className="parrainage-small">
                La semaine est accordée au premier abonnement payé de la personne parrainée (pas aux abonnements offerts).
                Le parrainage ne peut pas être utilisé pour soi-même ni après un premier achat.
            </p>
        </div>
    );
}
